import { Input } from "antd";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import { RequiredMark } from "@/shared/components/RequiredMark";

interface AuthFieldBaseProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
  className?: string;
  /** Iconify icon name shown as a left prefix inside the input (e.g. "lucide:mail"). */
  icon?: string;
  disabled?: boolean;
  /** Required field: a red asterisk after the label. */
  required?: boolean;
  /** "pill": the taller (60px), rounder, label-less field used on the focused
   *  auth screens (verification, reset password). The label stays for screen readers. */
  variant?: "default" | "pill";
}

const PILL_INPUT =
  "!h-[62px] !rounded-[20px] !border-[#ECECF1] !bg-white !px-5 !py-0 !text-sm !text-[#374151] !shadow-[0_1px_2px_rgba(17,24,39,0.04)] [&_input::placeholder]:!text-[#8B8F98] [&_.ant-input-password-icon]:!text-[17px] [&_.ant-input-password-icon]:!text-[#111827]";
const DEFAULT_INPUT = "!rounded-[14px] !border-[#E5E7EB] !px-4 !py-2.5 !text-sm !text-[#374151]";

// This field's exact look (rounded-[14px] — the pill field's 20px/62px corner
// scaled to this ~42px height, py-2.5 px-4, custom clear icon)
// diverges from the global Input theme (rounded-md = 6px, h-9), so it's
// styled locally via className rather than through shared Input tokens —
// forcing it through the global theme would change its radius/height.
export function AuthTextField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  error,
  className,
  icon,
  disabled,
  required,
}: AuthFieldBaseProps) {
  return (
    <div className={cn("mb-5", className)}>
      <label className="mb-2 block text-sm font-medium text-[#374151]">{label}{required && <RequiredMark />}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        status={error ? "error" : undefined}
        disabled={disabled}
        allowClear={!disabled}
        prefix={icon ? (
          <span className="mr-2.5 flex items-center gap-2.5">
            <Icon icon={icon} className="h-4 w-4 text-[#374151]" />
            <span className="h-5 w-px shrink-0 bg-[#DADDE5]" />
          </span>
        ) : undefined}
        className={cn(
          "!rounded-[14px] !border-[#E5E7EB] !px-4 !py-2.5 !text-sm !text-[#374151]",
          "[&.ant-input-status-error]:!border-[#E4002B]",
          "hover:!border-[#E4002B]/50 focus:!border-[#E4002B]/50 focus:!shadow-none",
        )}
      />
      {error && <p className="!mt-1 text-xs text-[#E4002B]">{error}</p>}
    </div>
  );
}

export function AuthPasswordField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  error,
  className,
  icon,
  disabled,
  variant = "default",
  required,
}: AuthFieldBaseProps) {
  const pill = variant === "pill";
  return (
    <div className={cn("mb-5", className)}>
      <label className={pill ? "sr-only" : "mb-2 block text-sm font-medium text-[#374151]"}>{label}{required && <RequiredMark />}</label>
      <Input.Password
        aria-label={pill ? label : undefined}
        // The design shows an open eye while the password is hidden ("show it").
        iconRender={pill ? (visible) => <Icon icon={visible ? "lucide:eye-off" : "lucide:eye"} className="h-[17px] w-[17px] cursor-pointer" /> : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        status={error ? "error" : undefined}
        disabled={disabled}
        prefix={icon ? (
          <span className="mr-2.5 flex items-center gap-2.5">
            <Icon icon={icon} className={cn("h-4 w-4", pill ? "text-[#111827]" : "text-[#374151]")} />
            <span className={cn("w-px shrink-0 bg-[#DADDE5]", pill ? "ml-1.5 h-[22px]" : "h-5")} />
          </span>
        ) : undefined}
        className={cn(
          pill ? PILL_INPUT : DEFAULT_INPUT,
          "[&.ant-input-status-error]:!border-[#E4002B]",
          "hover:!border-[#E4002B]/50 focus:!border-[#E4002B]/50 focus:!shadow-none",
        )}
      />
      {error && <p className="!mt-1 text-xs text-[#E4002B]">{error}</p>}
    </div>
  );
}
